import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { isNative, registerNativePush, unregisterNativePush, getSavedNativePushToken } from "@/lib/native";

// Convert URL-safe base64 → Uint8Array (needed by pushManager.subscribe)
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * usePushNotifications — helper that manages the "Enable push" UX.
 *
 * Returns:
 *  - supported: true if the browser has Notification + serviceWorker + PushManager.
 *  - permission: current Notification.permission value ("granted"|"denied"|"default").
 *  - subscribed: whether *this* browser is subscribed on this DPP account.
 *  - loading: transient state while enabling/disabling/checking.
 *  - enable(): request permission + subscribe.
 *  - disable(): unsubscribe locally + tell backend to drop the record.
 *  - sendTest(): fire a "hello" push to this device via the backend.
 */
export default function usePushNotifications() {
  const native = isNative();
  const supported = native || (typeof window !== "undefined"
    && "Notification" in window
    && "serviceWorker" in navigator
    && "PushManager" in window);

  const [permission, setPermission] = useState(() =>
    native ? (getSavedNativePushToken() ? "granted" : "default")
      : typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(() => native && !!getSavedNativePushToken());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported || native) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setSubscribed(!!sub);
      setPermission(Notification.permission);
    } catch (e) { /* noop */ }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = useCallback(async () => {
    if (!supported) { toast.error("Push notifications aren't supported on this browser"); return; }
    setLoading(true);
    if (native) {
      try {
        await registerNativePush();
        setPermission("granted");
        setSubscribed(true);
        toast.success("Notifications enabled — you're all set");
      } catch (err) {
        toast.error(err?.message || "Could not enable notifications");
      } finally { setLoading(false); }
      return;
    }
    try {
      // 1. Request permission (this shows the native prompt on the first call).
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notifications blocked — enable them in your browser settings");
        return;
      }
      // 2. Register the service worker (idempotent).
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      // 3. Fetch our public VAPID key.
      const { data } = await api.get("/push/vapid-public-key");
      const key = urlBase64ToUint8Array(data.public_key);
      // 4. Subscribe with the browser's push service.
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key,
      });
      const subJson = sub.toJSON();
      // 5. Ship the subscription to our backend so we can push to it later.
      await api.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Notifications enabled — you're all set");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Could not enable notifications");
    } finally { setLoading(false); }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    if (native) {
      await unregisterNativePush();
      setSubscribed(false);
      setLoading(false);
      toast.success("Notifications turned off");
      return;
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try { await api.delete("/push/subscribe", { params: { endpoint } }); } catch (_) {}
      }
      setSubscribed(false);
      toast.success("Notifications turned off");
    } catch (err) {
      toast.error(err?.message || "Could not disable notifications");
    } finally { setLoading(false); }
  }, [supported]);

  const sendTest = useCallback(async () => {
    try {
      const r = await api.post("/push/test", {});
      toast.success(`Test sent · ${r.data.ok} device${r.data.ok === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    }
  }, []);

  return { supported, permission, subscribed, loading, enable, disable, sendTest, refresh };
}
